import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogIn, UserCog, KeyRound, ArrowLeft } from "lucide-react";import {
  talentApi,
  setTalentSession,
  isTalentAuthenticated,
} from "../../lib/talentPortalApi";
import { Button } from "../../components/ui/button";
import { FormField } from "../../components/ui/form-field";
import { formatApiError } from "../../lib/api";

export default function TalentPortalLogin() {
  const nav = useNavigate();
  const [mode, setMode] = useState("login"); // "login" | "first"
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (isTalentAuthenticated()) nav("/portal-colaborador/painel", { replace: true });
  }, [nav]);

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } =
        mode === "login"
          ? await talentApi.login(email.trim(), password)
          : await talentApi.setPassword(email.trim(), phone.trim(), password);
      setTalentSession(data.token, data.talent);
      toast.success(
        mode === "login" ? "Bem-vindo(a) de volta!" : "Senha definida com sucesso!",
      );
      nav("/portal-colaborador/painel", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Não foi possível entrar.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-blue-50 flex items-center justify-center p-6">
      <a
        href="/"
        data-testid="tp-back-to-site"
        className="fixed top-4 left-4 inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white/80 backdrop-blur border border-slate-200 text-sm font-medium text-slate-700 hover:bg-white hover:text-emerald-700 shadow-sm transition"
      >
        <ArrowLeft size={16} /> Voltar ao site
      </a>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-emerald-600 text-white items-center justify-center mb-3 shadow-md">
            <UserCog size={28} />
          </div>
          <h1 className="text-3xl font-bold text-slate-900">Portal do Colaborador</h1>
          <p className="text-slate-600 mt-1">
            {mode === "login"
              ? "Acesse para gerenciar seus dados e disponibilidade."
              : "Primeiro acesso: confirme seus dados para criar uma senha."}
          </p>
        </div>

        <form
          onSubmit={submit}
          className="bg-white rounded-2xl shadow-xl border border-slate-100 p-7 space-y-4"
          data-testid="talent-portal-login-form"
        >
          <FormField label="E-mail" required>
            <input
              data-testid="tp-email"
              type="email"
              autoComplete="email"
              className="cm-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </FormField>

          {mode === "first" && (
            <FormField label="Telefone do cadastro" required>
              <input
                data-testid="tp-phone"
                type="tel"
                autoComplete="tel"
                className="cm-input"
                placeholder="Mesmo telefone informado no cadastro"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />
            </FormField>
          )}

          <FormField label={mode === "first" ? "Crie uma senha (mín. 6)" : "Senha"} required>
            <input
              data-testid="tp-password"
              type="password"
              autoComplete={mode === "first" ? "new-password" : "current-password"}
              className="cm-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
            />
          </FormField>

          <Button
            data-testid="tp-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
          >
            {mode === "login" ? <LogIn size={16} className="mr-2" /> : <KeyRound size={16} className="mr-2" />}
            {loading ? "Aguarde…" : mode === "login" ? "Entrar" : "Definir senha e entrar"}
          </Button>

          <div className="pt-2 text-center">
            {mode === "login" ? (
              <button
                type="button"
                data-testid="tp-goto-first"
                onClick={() => setMode("first")}
                className="text-sm font-medium text-emerald-700 hover:underline"
              >
                Primeiro acesso? Criar minha senha
              </button>
            ) : (
              <button
                type="button"
                data-testid="tp-goto-login"
                onClick={() => setMode("login")}
                className="inline-flex items-center gap-1 text-sm font-medium text-slate-600 hover:underline"
              >
                <ArrowLeft size={14} /> Já tenho senha
              </button>
            )}
          </div>
        </form>

        <p className="text-xs text-slate-400 text-center mt-4">
          Não é cadastrado?{" "}
          <a href="/seja-colaborador" className="text-emerald-700 hover:underline">
            Cadastre-se aqui
          </a>
        </p>
      </div>
    </div>
  );
}
